import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/shadcn/collapsible'
import { Button } from '@/components/shadcn/button'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/shadcn/hover-card'
import { cn } from '@/lib/utils'
import { IconDocumentOutline } from '@icons/IconDocumentOutline'
import { IconRightChevronOutline } from '@icons/IconRightChevronOutline'
import { IconV2DownloadOutline } from '@icons/IconV2DownloadOutline'
import { IconV2FolderOutline } from '@icons/IconV2FolderOutline'
import { IconV2InformationOutline } from '@icons/IconV2InformationOutline'
import { IconV2TrashOutline } from '@icons/IconV2TrashOutline'
import { Fragment, useState } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useRestClient } from '@/contexts/QueryProvider'
import { MessagesStorageStorageFileType } from '@/util/enums'
import { toast } from 'sonner'
import { downloadFile } from '../Forms/FilesList/downloadFile'
import { useCurrentWorkspace } from '../useCurrentWorkspace'
import type { WorkspaceFileTreeFileNode, WorkspaceFileTreeFolderNode, WorkspaceFileTreeNode } from './workspaceFileTree'

/** Base size for tree labels and leading icons */
const treeTextClass = 'text-base'
const treeLeadingIconClass = 'text-muted-foreground size-5 shrink-0'
const treeChevronClass = 'text-muted-foreground size-4 shrink-0 transition-transform duration-200'
const treeRowButtonClass =
  'h-auto min-h-9 w-full justify-start gap-2.5 px-2 py-2 text-base font-normal [&_svg:not([class*="size-"])]:size-5'
const treeActionIconClass = 'size-5'

export type WorkspaceFileTreeViewProps = {
  nodes: WorkspaceFileTreeNode[]
  className?: string
  onFileClick?: (file: WorkspaceFileTreeFileNode) => void
  /** Nested branch lists use `role="group"` per tree accessibility patterns */
  nested?: boolean
}

export function WorkspaceFileTreeView({ nodes, className, onFileClick, nested = false }: WorkspaceFileTreeViewProps) {
  if (nodes.length === 0) {
    return <p className={cn('px-1 py-6 text-muted-foreground', treeTextClass)}>No files to show.</p>
  }

  const defaultFolderOpen = !nested

  return (
    <ul className={cn('m-0 list-none space-y-0.5 p-0', treeTextClass, className)} role={nested ? 'group' : 'tree'}>
      {nodes.map((node) => (
        <WorkspaceFileTreeItem
          key={node.nodeKind === 'folder' ? node.path : node.blobName}
          node={node}
          defaultFolderOpen={defaultFolderOpen}
          onFileClick={onFileClick}
        />
      ))}
    </ul>
  )
}

function WorkspaceFileTreeItem({
  node,
  defaultFolderOpen,
  onFileClick,
}: {
  node: WorkspaceFileTreeNode
  defaultFolderOpen: boolean
  onFileClick?: (file: WorkspaceFileTreeFileNode) => void
}) {
  if (node.nodeKind === 'file') {
    return <WorkspaceFileTreeFileRow node={node} onFileClick={onFileClick} />
  }
  return <WorkspaceFileTreeFolderRow node={node} defaultFolderOpen={defaultFolderOpen} onFileClick={onFileClick} />
}

function WorkspaceFileTreeFileRow({
  node,
  onFileClick,
}: {
  node: WorkspaceFileTreeFileNode
  onFileClick?: (file: WorkspaceFileTreeFileNode) => void
}) {
  const fileRowBody = (
    <>
      <IconDocumentOutline className={cn(treeLeadingIconClass, 'mt-0.5')} />
      <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
        <span className="min-w-0 flex-1 truncate font-normal">{node.filename}</span>
        <WorkspaceFilePropertiesHoverCard blobName={node.blobName} properties={node.properties} />
      </span>
    </>
  )

  return (
    <li className="m-0 flex items-stretch gap-1 p-0 pr-1" role="treeitem">
      <div className="min-w-0 flex-1">
        {onFileClick ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(treeRowButtonClass, 'items-start')}
            onClick={() => onFileClick(node)}
          >
            {fileRowBody}
          </Button>
        ) : (
          <div className={cn(treeRowButtonClass, 'flex cursor-default items-start rounded-md')}>{fileRowBody}</div>
        )}
      </div>
      <WorkspaceFileTreeFileActions item={node} />
    </li>
  )
}

function WorkspaceFilePropertiesHoverCard({
  blobName,
  properties,
}: {
  blobName: string
  properties: { [key: string]: string }
}) {
  const entries = Object.entries(properties)

  return (
    <div className="shrink-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-foreground"
            aria-label="View blob path and file properties"
          >
            <IconV2InformationOutline className={treeActionIconClass} />
          </Button>
        </HoverCardTrigger>
        <HoverCardContent
          align="start"
          side="left"
          sideOffset={8}
          className="w-80 max-w-[min(20rem,calc(100vw-2rem))] p-3"
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm font-bold">Blob path:</p>
              <p className="break-all text-sm font-normal">{blobName}</p>
            </div>
            {entries.length > 0 ? (
              <div>
                <p className="text-sm font-bold">Properties:</p>
                <dl className="grid grid-cols-[minmax(0,auto)_1fr] gap-x-3 text-sm">
                  {entries.map(([key, value]) => (
                    <Fragment key={key}>
                      <dt className="font-medium text-muted-foreground">{key}:</dt>
                      <dd className="min-w-0 break-words font-normal">{value}</dd>
                    </Fragment>
                  ))}
                </dl>
              </div>
            ) : null}
          </div>
        </HoverCardContent>
      </HoverCard>
    </div>
  )
}

function WorkspaceFileTreeFileActions({ item }: { item: WorkspaceFileTreeFileNode }) {
  const { workspaceId } = useCurrentWorkspace()
  const { getStorageApiGetFilesDetailsWorkspace, postStorageApiDeleteFileWorkspace } = useRestClient()
  const { mutate: deleteFile, isPending: isDeleteFilePending } = postStorageApiDeleteFileWorkspace.useMutation()

  const invalidateFiles = () =>
    getStorageApiGetFilesDetailsWorkspace.invalidateQueries({
      parameters: { query: { workspaceId } },
    })

  const onDeleteConfirm = () => {
    deleteFile(
      {
        query: {
          blobName: item.encryptedBlobName,
          subscriptionId: workspaceId,
          fileType: MessagesStorageStorageFileType.Template,
        },
      },
      {
        onSuccess: () => {
          toast.success('File deleted.')
          invalidateFiles()
        },
        onError: () => {
          toast.error('File was not deleted.')
        },
      }
    )
  }

  return (
    <div
      className="flex shrink-0 items-center gap-0.5 self-center"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-foreground"
            aria-label="Download file"
            onClick={() => downloadFile(item, workspaceId)}
          >
            <IconV2DownloadOutline className={treeActionIconClass} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" sideOffset={6}>
          Download
        </TooltipContent>
      </Tooltip>
      <ConfirmDialog
        triggerTooltip="Delete"
        triggerTooltipSide="top"
        trigger={
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-destructive"
            disabled={isDeleteFilePending}
            aria-label="Delete file"
          >
            <IconV2TrashOutline className={treeActionIconClass} />
          </Button>
        }
        confirmVariant="destructive"
        confirmLabel="Delete"
        onConfirm={onDeleteConfirm}
        title="Delete file"
        description="Are you sure you want to delete the file?"
      />
    </div>
  )
}

function WorkspaceFileTreeFolderRow({
  node,
  defaultFolderOpen,
  onFileClick,
}: {
  node: WorkspaceFileTreeFolderNode
  defaultFolderOpen: boolean
  onFileClick?: (file: WorkspaceFileTreeFileNode) => void
}) {
  const hasChildren = node.children.length > 0

  if (!hasChildren) {
    return (
      <li className="m-0 p-0" role="treeitem">
        <div
          className={cn(
            'flex w-full cursor-default items-center gap-2.5 px-2 py-2 text-muted-foreground',
            treeTextClass
          )}
        >
          <IconV2FolderOutline className={treeLeadingIconClass} />
          <span className="min-w-0 flex-1 truncate text-foreground">{node.name}</span>
        </div>
      </li>
    )
  }

  return <WorkspaceFileTreeFolderBranch node={node} defaultOpen={defaultFolderOpen} onFileClick={onFileClick} />
}

function WorkspaceFileTreeFolderBranch({
  node,
  defaultOpen,
  onFileClick,
}: {
  node: WorkspaceFileTreeFolderNode
  defaultOpen: boolean
  onFileClick?: (file: WorkspaceFileTreeFileNode) => void
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <li className="m-0 p-0" role="treeitem" aria-expanded={open}>
      <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
        <CollapsibleTrigger asChild>
          <Button type="button" variant="ghost" size="sm" className={treeRowButtonClass}>
            <IconV2FolderOutline className={treeLeadingIconClass} />
            <span className="min-w-0 flex-1 truncate text-left">{node.name}</span>
            <IconRightChevronOutline className={cn(treeChevronClass, open && 'rotate-90')} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <div className="ml-3 border-l border-border pl-2 pt-0.5">
            <WorkspaceFileTreeView nested nodes={node.children} onFileClick={onFileClick} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  )
}
